/**
 * Operator DTOs exposed by operator-service `/api/operators` and
 * `/api/admin/operators`. Mirrors `OperatorView` + `Operator` entity.
 */

import { defensiveStatusMeta } from '../helpers/status-meta';
import type { UserRef } from './user.types';

export type OperatorStatus = 'PENDING' | 'ACTIVE' | 'EXHAUSTED' | 'SUSPENDED';
export type LicenseTier = 'BRONZE' | 'SILVER' | 'GOLD';
export type PrestataireType = 'OWNER' | 'DRIVER';

/** Row shape returned by `GET /api/operators` (paginated list). */
export interface OperatorView {
  id: number;
  userId: number;
  businessName: string | null;
  zoneCode: string | null;
  licenseTier: LicenseTier | null;
  isFallback: boolean | null;
  isSystemOwned: boolean | null;
  operatorStatus: OperatorStatus;
  /** Dossier KYC (#66) — prérequis au recrutement (403 back si ≠ VALIDATED). */
  kycStatus?: OperatorKycStatus;
  /** Motif du rejet, renseigné uniquement quand `kycStatus === 'REJECTED'`. */
  kycRejectionReason?: string | null;
  customTotalPct?: number | null;
  customPlatformPct?: number | null;
  customOperatorPct?: number | null;
  creditReferenceAmount?: number | null;
  /** Read-model du gérant. Présent depuis le patch back qui enrichit `OperatorView`. */
  user?: UserRef | null;
}

/** Full Operator entity returned by `GET /api/operators/{id}` (richer fields). */
export interface Operator extends OperatorView {
  // BaseEntity fields
  createdAt?: string | null;
  updatedAt?: string | null;
  // Extra fields exposed via detail view
  creditThresholdState?: number | null;
  phone?: string | null;
  email?: string | null;
  /** Common BaseEntity zone — already in zoneCode but back also has zoneId. */
  zoneId?: number | null;
}

/** Body of `POST /api/operators/{id}/approve`. All fields optional. */
export interface ApproveOperatorRequest {
  licenseTier?: LicenseTier | string;
  customTotalPct?: number | null;
  customPlatformPct?: number | null;
  customOperatorPct?: number | null;
}

/**
 * Body of `POST /api/operators/apply` — candidature self-service de l'utilisateur
 * courant pour devenir opérateur. Aucun rôle requis (JWT seul). Le back crée un
 * Operator `PENDING` ; le rôle OPERATOR n'est assigné qu'à l'approbation admin.
 */
export interface ApplyOperatorRequest {
  businessName: string;
  zoneCode: string;
  licenseTier?: LicenseTier | null;
  phone?: string | null;
  email?: string | null;
}

/** Body of `POST /api/admin/operators` — admin creates an operator for a userId. */
export interface AdminCreateOperatorRequest {
  userId: number;
  businessName: string;
  zoneCode: string;
  licenseTier?: LicenseTier | null;
  phone?: string | null;
  email?: string | null;
}

/** Body of `PATCH /api/admin/operators/{id}` — partial update of business fields.
 *  Only non-null fields are applied (back ignores `null`). */
export interface AdminUpdateOperatorRequest {
  businessName?: string | null;
  zoneCode?: string | null;
  licenseTier?: LicenseTier | null;
  phone?: string | null;
  email?: string | null;
}

/** Returned by `GET /api/operators/{id}/effective-rates`. Combines
 *  the operator's custom override (if any) with the global defaults
 *  from `CommissionConfig`. */
export interface EffectiveRatesResponse {
  operatorId: number;
  totalPct: number;
  platformPct: number;
  operatorPct: number;
  /** true if the override is applied, false if globals are in effect. */
  usesCustomRates: boolean;
}

/** Body of `PUT /api/admin/operators/{id}/credit-reference`. */
export interface CreditReferenceRequest {
  /** `null` removes the override and falls back to the global default. */
  amount: number | null;
  /** Audit reason, free-text. */
  reason: string;
}

/**
 * Snapshot du tableau de bord opérateur — `GET /api/operators/me/dashboard`.
 * Réponse brute (pas d'enveloppe Nawam). Mirroir de `OperatorDashboardResponse`
 * côté operator-service. Les montants sont en XOF.
 */
/**
 * Statut du dossier KYC de l'opérateur (#66) — prérequis au recrutement.
 * `PENDING` (rien de soumis) → `UNDER_REVIEW` (en attente admin) →
 * `VALIDATED` (recrutement débloqué) | `REJECTED` (motif + re-soumission).
 */
export type OperatorKycStatus = 'PENDING' | 'UNDER_REVIEW' | 'VALIDATED' | 'REJECTED';

export interface OperatorDashboardResponse {
  operatorId: number;
  businessName: string | null;
  zoneCode: string | null;
  operatorStatus: OperatorStatus;
  /** Dossier KYC (#66). Le recrutement n'est autorisé que si `VALIDATED` (sinon 403 back). */
  kycStatus: OperatorKycStatus;
  /** Motif du rejet, renseigné uniquement quand `kycStatus === 'REJECTED'`. */
  kycRejectionReason?: string | null;
  /** CREDIT = réserve de fonctionnement (consommée par la fallback / commissions). */
  creditBalance: number;
  /** BALANCE = revenus accumulés (commissions perçues). */
  balanceBalance: number;
  /** Part de la BALANCE réellement retirable (BALANCE − dette éventuelle). */
  withdrawableBalance: number;
  /** Nombre de prestataires liés actifs (OWNER + DRIVER). */
  activePrestataireCount: number;
  /**
   * Plafond de prestataires de la licence ACTIVE (snapshoté à l'achat).
   * `null` = illimité → le front affiche « 8 / ∞ ».
   */
  maxPrestataires: number | null;
  /** Somme des commissions créditées depuis le 1er du mois calendaire courant. */
  commissionsThisMonth: number;
  /** Nombre de prestataires liés dont le CREDIT est en dette (< 0). */
  prestatairesInDebtCount: number;
}

/**
 * Rapport de réconciliation cash hebdomadaire de l'opérateur (§8.4). Généré par
 * un cron chaque semaine (`generatedAt`). Mirroir de `CashReconciliationReport`
 * côté operator-service. Montants en XOF.
 */
export interface CashReconciliationReport {
  id: number;
  operatorId: number;
  /** Début (inclus) de la semaine couverte. */
  weekStart: string;
  /** Fin (exclue) de la semaine couverte. */
  weekEnd: string;
  /** Somme des commissions perçues sur la semaine. */
  totalCommissions: number;
  /** Nombre de prestataires actifs sur la période. */
  prestatairesCount: number;
  /** Nombre de prestataires en dette (CREDIT < 0) au moment du calcul. */
  prestatairesInDebtCount: number;
  /**
   * Montant total dû par les prestataires en dette (somme des CREDIT négatifs,
   * en positif) au moment de la génération (#47). Le « combien » qui complète
   * `prestatairesInDebtCount` (le « qui »).
   */
  totalDebtAmount: number;
  /**
   * Nombre de dettes réglées **en espèces** (déclarées) sur la semaine (#49) —
   * l'opérateur a attesté un règlement cash d'un prestataire.
   */
  debtsSettledCashCount: number;
  /**
   * Nombre de dettes réglées **en digital** (recharge CREDIT) sur la semaine (#49) —
   * épongées automatiquement au top-up.
   */
  debtsSettledDigitalCount: number;
  /** Montant réglé en espèces sur la semaine (dettes soldées `paidAt ∈ semaine`, #50). */
  debtsSettledCashAmount: number;
  /** Montant réglé en digital sur la semaine (#50). */
  debtsSettledDigitalAmount: number;
  /** Snapshot d'aging du restant dû — tranche 0-7 j (#50). */
  outstandingAging0_7: number;
  /** Snapshot d'aging du restant dû — tranche 8-30 j (#50). */
  outstandingAging8_30: number;
  /** Snapshot d'aging du restant dû — tranche > 30 j (#50). */
  outstandingAging31Plus: number;
  /** Horodatage de génération du rapport. */
  generatedAt: string;
}

/**
 * Rapprochement « dû vs réglé » + aging vu par l'opérateur (#50) — « combien mes
 * prestataires me doivent, combien réglé (cash/digital), combien reste, depuis
 * quand ». Miroir de `OperatorDebtReconciliationView`. Montants en XOF.
 */
export interface OperatorDebtReconciliationView {
  operatorId: number;
  businessName: string | null;
  fallback: boolean;
  /** Dû total (Σ montants d'origine). */
  owed: number;
  settledCash: number;
  settledDigital: number;
  settledTotal: number;
  /** Restant dû (Σ dettes UNPAID). */
  outstanding: number;
  totalCount: number;
  unpaidCount: number;
  aging0_7: number;
  aging8_30: number;
  aging31Plus: number;
  currency: string;
}

// ── UI helpers ─────────────────────────────────────────────────────────

export const OPERATOR_STATUS_META = defensiveStatusMeta<OperatorStatus>({
  PENDING: { label: 'status.operator.PENDING', palette: 'warning' },
  ACTIVE: { label: 'status.operator.ACTIVE', palette: 'brand' },
  EXHAUSTED: { label: 'status.operator.EXHAUSTED', palette: 'error' },
  SUSPENDED: { label: 'status.operator.SUSPENDED', palette: 'gray' },
});

export const LICENSE_TIER_LABEL: Record<LicenseTier, string> = {
  BRONZE: 'label.licenseTier.BRONZE',
  SILVER: 'label.licenseTier.SILVER',
  GOLD: 'label.licenseTier.GOLD',
};

export const LICENSE_TIER_OPTIONS: Array<{ value: LicenseTier; label: string }> = [
  { value: 'BRONZE', label: LICENSE_TIER_LABEL.BRONZE },
  { value: 'SILVER', label: LICENSE_TIER_LABEL.SILVER },
  { value: 'GOLD', label: LICENSE_TIER_LABEL.GOLD },
];
